import { Injectable } from '@angular/core';
import { DocumentCategoryHandlerService } from "../handlers/document-category-handler.service";
import { DocumentCategoryDTO } from "./DocumentCategory";
import { DocumentHandlerService } from "../handlers/document-handler.service";
import { DocumentDTO } from "./Document";

@Injectable({
  providedIn: 'root'
})
export class AssististService {

  constructor(
    private DocumentCategoryHandlerService: DocumentCategoryHandlerService,
    private DocumentHandlerService: DocumentHandlerService,
  ) { }

  /**
   * List/Search Document Categories with optional paging, sorting, filters, or count-only.
   */
  listDocumentCategories(
    masterAccountID: number,
    integrationID: number,
    queryParams?: {
      page?: number;
      perPage?: number;
      sort?: string | string[];
      keyword?: string;
      filter_columns?: string[];
      forCount?: boolean;
    }
  ) {
    return this.DocumentCategoryHandlerService.getDocumentCategories(masterAccountID, integrationID, queryParams);
  }

  /**
   * Get a single Document Category by ID.
   */
  getDocumentCategory(masterAccountID: number, integrationID: number, documentCategoryID: number) {
    return this.DocumentCategoryHandlerService.getDocumentCategory(masterAccountID, integrationID, documentCategoryID);
  }

  /**
   * Create a new Document Category.
   */
  createDocumentCategory(
    masterAccountID: number,
    integrationID: number,
    documentCategory: Partial<DocumentCategoryDTO> & { name: string; media_id?: number | null }
  ) {
    return this.DocumentCategoryHandlerService.postDocumentCategory(masterAccountID, integrationID, documentCategory as DocumentCategoryDTO);
  }

  /**
   * Update an existing Document Category.
   */
  updateDocumentCategory(masterAccountID: number, integrationID: number, documentCategory: DocumentCategoryDTO) {
    return this.DocumentCategoryHandlerService.putDocumentCategory(masterAccountID, integrationID, documentCategory);
  }

  /**
   * Delete a Document Category by ID.
   */
  deleteDocumentCategory(masterAccountID: number, integrationID: number, documentCategoryID: number) {
    return this.DocumentCategoryHandlerService.deleteDocumentCategory(masterAccountID, integrationID, documentCategoryID);
  }

  /**
   * List/Search Documents with optional paging, sorting, filters, or count-only.
   */
  listDocuments(
    masterAccountID: number,
    integrationID: number,
    queryParams?: {
      page?: number;
      perPage?: number;
      sort?: string | string[];
      keyword?: string;
      filter_columns?: string[];
      category_search?: string;
      tag_search?: string;
      search_mode?: 'AND' | 'OR';
      forCount?: boolean;
    }
  ) {
    return this.DocumentHandlerService.getDocuments(masterAccountID, integrationID, queryParams);
  }

  /**
   * Get a single Document by ID.
   */
  getDocument(masterAccountID: number, integrationID: number, documentID: number) {
    return this.DocumentHandlerService.getDocument(masterAccountID, integrationID, documentID);
  }

  /**
   * Create a new Document.
   */
  createDocument(
    masterAccountID: number,
    integrationID: number,
    document: Partial<DocumentDTO> & {
      name: string;
      subtitle?: string;
      preview?: string;
      content?: string;
      media_id?: number | null;
      url?: string | null;
      foreign_key?: string | null;
      booking_engine_integration_id?: number | null;
      publish?: boolean;
    }
  ) {
    return this.DocumentHandlerService.postDocument(masterAccountID, integrationID, document as DocumentDTO);
  }

  /**
   * Update an existing Document.
   */
  updateDocument(masterAccountID: number, integrationID: number, document: DocumentDTO) {
    return this.DocumentHandlerService.putDocument(masterAccountID, integrationID, document);
  }

  /**
   * Generate mock booking data preview without persisting document changes.
   */
  generateDocumentMockBookingData(
    masterAccountID: number,
    integrationID: number,
    payload: {
      document_id?: number | null;
      name?: string | null;
      subtitle?: string | null;
      preview?: string | null;
      foreign_key?: string | null;
      booking_engine_integration_id?: number | null;
      prompt?: string | null;
      current_mock_data?: any;
    }
  ) {
    return this.DocumentHandlerService.postGenerateMockBookingData(masterAccountID, integrationID, payload);
  }

  /**
   * Clear persisted mock bookings for a document.
   */
  clearDocumentMockBookings(masterAccountID: number, integrationID: number, documentID: number) {
    return this.DocumentHandlerService.deleteMockBookings(masterAccountID, integrationID, documentID);
  }

  /**
   * Delete a Document by ID.
   */
  deleteDocument(masterAccountID: number, integrationID: number, documentID: number) {
    return this.DocumentHandlerService.deleteDocument(masterAccountID, integrationID, documentID);
  }

  /**
   * Get categories and tags preview data.
   */
  getCategoriesTags(masterAccountID: number, integrationID: number) {
    return this.DocumentHandlerService.getCategoriesTags(masterAccountID, integrationID);
  }

  /**
   * Get autocomplete suggestions for tags in a specific document category.
   */
  getTagsAutocomplete(
    masterAccountID: number,
    integrationID: number,
    documentCategoryID: number,
    keyword?: string
  ) {
    return this.DocumentCategoryHandlerService.getTagsAutocomplete(
      masterAccountID,
      integrationID,
      documentCategoryID,
      { keyword }
    );
  }
} 